/*
 Copyright (c) 2025. BrightNight. All rights reserved.
 Unauthorized copying of this file, via any medium is strictly prohibited.
 Proprietary and confidential.
 */

import type { FlowRunStatus, ListResultsQuery } from '@poweralpha/common';
import {
  type InfiniteData,
  keepPreviousData,
  type UseMutationOptions,
  useInfiniteQuery,
  useMutation,
} from '@tanstack/react-query';
import type { HTTPError } from 'ky';
import { sum } from 'lodash';
import { api, getRunsStatus } from '../api';
import { isFlowRunning, PAGE_SIZE } from './const';

export const useGetScenarioIdMutation = (
  opts?: UseMutationOptions<Array<{ scenario_id: string }>, HTTPError, Array<string>>,
) =>
  useMutation<Array<{ scenario_id: string }>, HTTPError, Array<string>>({
    ...opts,
    mutationFn: async (runIds) => api.get(`results/run-ids?runId=${runIds.join('&runId=')}`).json(),
  });

export const getSimulationInfiniteQueryKey = () => ['simulation-status'] as const;

export const useSimulationInfiniteQuery = () =>
  useInfiniteQuery<
    ListResultsQuery<FlowRunStatus>,
    HTTPError,
    InfiniteData<ListResultsQuery<FlowRunStatus>> & { numRunning: number },
    ReturnType<typeof getSimulationInfiniteQueryKey>,
    number
  >({
    queryKey: getSimulationInfiniteQueryKey(),
    queryFn: ({ pageParam = PAGE_SIZE }) =>
      getRunsStatus({
        queryByUser: true,
        limit: PAGE_SIZE,
        offset: pageParam - PAGE_SIZE,
      }),
    select: (data) => {
      const reversedPages: Array<ListResultsQuery<FlowRunStatus>> = [];
      data.pages.forEach((page) => {
        const reversedPage = [...page.rows];
        reversedPage.reverse();
        reversedPages.push({ ...page, rows: reversedPage });
      });
      reversedPages.reverse();
      return {
        ...data,
        pages: reversedPages,
        numRunning: sum(data.pages.flatMap((res) => res.rows.filter(isFlowRunning).length)),
      };
    },
    initialPageParam: PAGE_SIZE,
    getNextPageParam: (_, p) => sum(p.flatMap((x) => x.rows.length)) + PAGE_SIZE,
    refetchInterval: ({ state }) =>
      sum(state.data?.pages.flatMap((res) => res.rows.filter(isFlowRunning).length)) > 0
        ? 5e3
        : false,
    placeholderData: keepPreviousData,
  });
